import React from "react";
import { Pressable, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import AppSwitch from "@/components/shared/AppSwitch";
import HelpTip from "./HelpTip";
import { colors, text } from "./theme";

// Rows for on/off settings and choices.
//
//   <RowGroup>
//     <ToggleRow label="Live only" description="No replay, no rewind" help="privacy.liveOnly" value={…} onValueChange={…} />
//   </RowGroup>
//   <ChoiceGroup value={…} onChange={…} options={[{ value, label, description }]} />
//
// Label left (with its help tip and a muted description), switch right. Rows
// are separated by hairlines; the whole row is the touch target.

export const RowGroup = ({ children, style }) => (
  <View style={[{ borderTopWidth: 1, borderTopColor: colors.divider }, style]}>
    {children}
  </View>
);

export const ToggleRow = ({
  label,
  description,
  help,
  value,
  onValueChange,
  disabled,
  style,
  testID,
}) => (
  <Pressable
    onPress={() => !disabled && onValueChange(!value)}
    accessibilityRole="switch"
    accessibilityState={{ checked: !!value, disabled: !!disabled }}
    accessibilityLabel={label}
    testID={testID}
    style={[
      tw`flex-row items-center py-3`,
      { borderBottomWidth: 1, borderBottomColor: colors.divider },
      disabled && { opacity: 0.6 },
      style,
    ]}
  >
    <View style={tw`flex-1 pr-4`}>
      <View style={tw`flex-row items-center`}>
        <Text style={[text.body, tw`flex-shrink`]}>{label}</Text>
        {!!help && <HelpTip topic={help} />}
      </View>
      {!!description && (
        <Text style={[text.hint, tw`mt-0.5`]}>{description}</Text>
      )}
    </View>
    <AppSwitch
      isOn={!!value}
      disabled={!!disabled}
      onColor={colors.brand}
      onToggle={(v) => !disabled && onValueChange(v)}
    />
  </Pressable>
);

const Radio = ({ selected, disabled }) => (
  <View
    style={[
      tw`items-center justify-center rounded-full`,
      {
        width: 20,
        height: 20,
        borderWidth: 2,
        borderColor: disabled
          ? colors.divider
          : selected
          ? colors.brand
          : colors.subtle,
        marginTop: 0,
      },
    ]}
  >
    {selected && (
      <View
        style={[
          tw`rounded-full`,
          { width: 10, height: 10, backgroundColor: colors.brand },
        ]}
      />
    )}
  </View>
);

export const ChoiceGroup = ({
  value,
  onChange,
  options,
  style,
  accessibilityLabel,
}) => (
  <View
    accessibilityRole="radiogroup"
    accessibilityLabel={accessibilityLabel}
    style={style}
  >
    {options.map((option) => {
      const selected = option.value === value;
      return (
        <View key={String(option.value)}>
          <Pressable
            onPress={() => !option.disabled && onChange(option.value)}
            accessibilityRole="radio"
            accessibilityState={{
              checked: selected,
              disabled: !!option.disabled,
            }}
            accessibilityLabel={option.label}
            style={({ hovered }) => [
              tw`flex-row items-start py-2.5 px-2 -mx-2 rounded-lg`,
              hovered &&
                !option.disabled && { backgroundColor: colors.surface },
            ]}
          >
            <View style={tw`pt-[0px]`}>
              <Radio selected={selected} disabled={option.disabled} />
            </View>
            <View style={tw`flex-1 ml-3`}>
              <View style={tw`flex-row items-center`}>
                <Text
                  style={[
                    selected ? text.bodyStrong : text.body,
                    option.disabled && { color: colors.subtle },
                  ]}
                >
                  {option.label}
                </Text>
                {!!option.help && <HelpTip topic={option.help} />}
              </View>
              {!!option.description && (
                <Text style={[text.hint, tw`mt-0.5`]}>
                  {option.description}
                </Text>
              )}
            </View>
          </Pressable>
          {selected && option.children ? (
            <View style={tw`ml-8 mb-2`}>{option.children}</View>
          ) : null}
        </View>
      );
    })}
  </View>
);

// A row that opens another screen or an outside page.
export const NavRow = ({
  icon,
  label,
  description,
  onPress,
  external,
  style,
}) => (
  <Pressable
    onPress={onPress}
    accessibilityRole={external ? "link" : "button"}
    style={({ hovered, pressed }) => [
      tw`flex-row items-center px-3 py-3 rounded-lg`,
      {
        backgroundColor:
          hovered || pressed ? colors.surfaceStrong : colors.surface,
      },
      style,
    ]}
  >
    {!!icon && (
      <Ionicons name={icon} size={20} color={colors.slate} style={tw`mr-3`} />
    )}
    <View style={tw`flex-1`}>
      <Text style={text.body}>{label}</Text>
      {!!description && (
        <Text style={[text.hint, tw`mt-0.5`]}>{description}</Text>
      )}
    </View>
    <Ionicons
      name={external ? "open-outline" : "chevron-forward"}
      size={16}
      color={colors.subtle}
    />
  </Pressable>
);
